<template>
   <div class='energy-base-line-mhp' :class="{disabled: Boolean(panelTooltip)}" :title="panelTooltip">
      <process-header :step="3"/>
      <div class="body">
         <div class='step-title'>
            <span>Energy Baseline (MHP)</span>
            <add-button @click="addMHPProject">
               Add MHP
            </add-button>
         </div>
         <el-table
            @cell-mouse-enter="highlightMHP"
            @cell-mouse-leave="clearHighlight"
            :row-class-name="getRowClassNames"
            :data="tableData"
            >
            <el-table-column label="Actions" fixed>
               <template slot-scope="scope">
                  <el-button
                     @click="handleSelect(scope.row)"
                     size="mini"
                     icon="el-icon-tickets"
                     style="margin: 1px;"
                     circle
                     content="Show Results"
                     v-tippy
                  />
                  <el-button
                     @click="handleEdit(scope.$index, scope.row)"
                     size="mini"
                     icon="el-icon-edit"
                     style="margin: 3px;"
                     circle
                     content="Edit"
                     v-tippy
                  />
                  <el-button
                     @click="handleDelete(scope.$index, scope.row)"
                     size="mini"
                     icon="el-icon-delete"
                     content="Delete"
                     v-tippy
                     circle
                  />
               </template>
            </el-table-column>
            <el-table-column
               v-for="{label, prop, width} in tableColumns"
               :key="prop"
               :prop="prop"
               :width="width"
               :label="label"
            />
         </el-table>
         <back-and-continue
            :back-route="{name: 'energy-baseline-bat'}"
            :next-route="{name: 'vision-mission'}"
         />
      </div>
      <common-dialog-form
         class='mhp-form-dialog'
         :visible="showFormDialog"
         title="MHP Processing Parameters"
         @close="showFormDialog=false"
         @submit="handleFormSubmit"
         >
         <input-field
            label="Name"
            type="text"
            v-model="mhp_form.name"
            required
         />
         <input-field
            label="Power (kW)"
            type="number"
            :extra-attributes="{step: 0.001, min: 0.001}"
            v-model="mhp_form.power_kW"
            required
         />
         <input-field
            label="MHP Interval (kW)"
            type="number"
            :extra-attributes="{step: 0.001, min: 0.001}"
            v-model="mhp_form.mhp_interval"
            required
         />
         <input-field
            label="Discharge (m3/s)"
            type="number"
            :extra-attributes="{step: 0.001, min: 0.001}"
            v-model="mhp_form.discharge"
            required
         />
      </common-dialog-form>
   </div>
</template>

<script>
import ProcessHeader from "../Header";
import AddButton from '@/components/molecules/Home/Plan/AddButton';
import CommonDialogForm from '@/components/organisms/Home/CommonDialogForm';
import BackAndContinue from '@/components/molecules/Home/Plan/Back&Continue';
import InputField from '@/components/molecules/Login/InputField';

import { mapActions, mapGetters } from 'vuex';
import { Table,TableColumn,Button } from 'element-ui';

import { selectMhpTool, handleToolModeSelection } from '../../../../../js/tools/mhp';
import { selectMunicipalityFromTop } from '../../../../../js/mapNavigator';
import { bus } from '../../../../../main';
import { fetchWithAuthToken, postWithAuthToken } from '../../../../../axios';
import { hideLoading, showError, showLoading, showSuccess,confirm } from '../../../../../js/ui-helpers';
import { getLayerById, getStyle } from '../../../../../js/layers';
import { fitToExtent, getMap } from '../../../../../js/map';

export default {
   name: "energy-baseline-mhp",
   components: {
      ProcessHeader,
      CommonDialogForm,
      AddButton,
      BackAndContinue,
      InputField,
      'el-table': Table,
      'el-table-column': TableColumn,
      'el-button': Button,
   },
   data()
   {
      const tableColumns = [
         {label: 'MHP Name', prop: 'name', width: 200},
         {label: 'Settlement Name', prop: 'local_name', width: 200},
         {label: 'MHP Interval (kW)', prop: 'mhp_interval', width: 150},
         {label: 'Total Power(kW)', prop: 'power_kW', width: 150},
      ];

      return {
         tableColumns,
         tableData: [],
         hh_input: 'osm',
         useOtherParameters: false,
         selectedMhpId: null,
         mhp_form: {
            power_kW: null,
            mhp_interval: null,
            discharge: null,
            plan_id: this.planId,
         },
         categorizedLoadInfos: [],
         showFormDialog: false,
         panelTooltip: '',
      };
   },
   computed: {
      ...mapGetters('plan', ['getPlan']),
      ...mapGetters('tools', ['getSstResults']),
      planId()
      {
         return this.$route.params.id;
      },
   },
   created()
   {
      this.setMhpResultsToShow(null);
      this.loadAllMHPProjects();
      bus.$on('stream-selected', ({dam_pt_approx, arc_id, discharge}) =>
      {
         this.enablePanel();
         this.mhp_form.dam_pt_approx = dam_pt_approx;
         this.mhp_form.arc_id = arc_id;
         this.mhp_form.discharge = discharge;
         this.showFormDialog = true;
      });
      bus.$on('mhp-draw-canceled', () => this.enablePanel());
   },
   beforeDestroy()
   {
      this.setMhpResultsToShow(null);
      handleToolModeSelection(false);
   },
   mounted() {
      this.updatePlanLayerVisibilityFor({mhp: true});
   },
   methods: {
      ...mapActions("plan", ["setMhpResultsToShow"]),
      ...mapActions("layers", ["updatePlanLayersFeatures", "updatePlanLayerVisibilityFor"]),
      getRowClassNames({row})
      {
         if (row.id===this.selectedBatId)
            return 'selected-row';
         return '';
      },
      highlightMHP(row)
      {
         const layer = getLayerById('plan_mhp_intakes', getMap())
         layer.setStyle(feature  =>
         {
            const isSelected = feature.get('mhp_id')===row.id;
            const stroke = isSelected ? {color: '#0083f46f', width: 150} : {color: '#0083f4'};
            return getStyle({
               geom: 'Point',
               stroke,
               fill: {color: '#0083f4'},
               radius: 5,
               text: {text: 'Intake'}
            });
         });
      },
      clearHighlight()
      {
         const layer = getLayerById('plan_mhp_intakes', getMap())
         layer.setStyle( () => getStyle({
            geom: 'Point',
            stroke: {color: '#0083f4'},
            fill: {color: '#0083f4'},
            radius: 5,
            text: {text: 'Intake'}
         }));
      },
      disablePanel()
      {
         // toolTip is used to enable and disable the panel
         this.panelTooltip = `MHP tool selected\nStart drawing in the map to add mhp\n\nPress Esc to quit`;
      },
      enablePanel()
      {
         this.panelTooltip = '';
      },
      addMHPProject()
      {
         this.disablePanel();
         selectMunicipalityFromTop(this.getPlan.municipality.sddmm);
         selectMhpTool();
         this.resetMHPForm();
      },
      getLoadInfos()
      {
         return Object.values(this.categorizedLoadInfos).reduce((acc,ctgry) => {
            return [...acc, ...ctgry.load_infos];
         }, []);
      },
      loadAllMHPProjects()
      {
         fetchWithAuthToken(`api/mhp/${this.planId}`)
         .then(({data}) =>
         {
            this.tableData = data;
            this.selectedMhpId = null;
            this.setMhpResultsToShow(null);
         });
      },
      resetMHPForm()
      {
         this.hh_input = 'osm';
         this.useOtherParameters = false;
         this.mhp_form = {
            id: null,
            plan_id: this.planId,
            power_kW: 5,
            mhp_interval: 5,
            discharge: 0.02,
            dam_pt_approx: null,
            arc_id: null,
         };
      },
      handleSelect(row)
      {
         showLoading('loading mhp project...');
         fetchWithAuthToken(`api/mhp/calculate?mhp_id=${row.id}`)
         .then(({data}) =>
         {
            this.setMhpResultsToShow(data);
            this.selectedMhpId = row.id;
            data.extent && fitToExtent(data.extent);
            showSuccess(`MHP (${row.name}) Selected`);
         })
         .catch(() => showError(`MHP (${row.name}) Selection Failed`))
         .finally(hideLoading);
      },
      handleEdit(index, row)
      {
         this.disablePanel();
         selectMunicipalityFromTop(this.getPlan.municipality.sddmm);
         selectMhpTool();
         this.resetMHPForm();
         this.mhp_form = {
            id: row.id,
            name: row.name,
            plan_id: this.planId,
            power_kW: row.power_kW,
            mhp_interval: row.mhp_interval,
            discharge: row.discharge,
            dam_pt_approx: null,
            arc_id: null,
         };
      },
      handleDelete(index, row)
      {
         const deleteMHPCluster = () => 
         {
            showLoading('Deleting...');
            postWithAuthToken('api/mhp/calculate', {'id': row.id}, {method: 'delete'})
            .then( () => {
               showSuccess("Energy Baseline(MHP)", `Successfully deleted MHP project`);
               this.loadAllMHPProjects();
               this.updatePlanLayersFeatures(this.planId);
            })
            .catch( () => showError("Energy Baseline(MHP)", `Failed deleting MHP project`))
            .finally( () => hideLoading() );
         };
         confirm({
            title: `Delete MHP?`,
            msg: `Are you sure you want to delete this MHP project: ${row.name}?`,
            onSuccess: deleteMHPCluster,
         });

      },
      handleFormSubmit()
      {
         this.showFormDialog = false;
         const method = this.mhp_form.id ? 'put' : 'post';  
         showLoading('Processing...');
         postWithAuthToken('api/mhp/calculate', {
            ...this.mhp_form,
            save_project: true,
         },{ method: method })
         .then(() =>
         {
            this.loadAllMHPProjects();
            this.updatePlanLayersFeatures(this.planId);
            hideLoading();
            showSuccess("Energy Baseline (MHP)", `Successfully ${method==='put' ? 'edited' : 'added'} a mhp project`);

         })
         .catch(() =>
         {
            hideLoading();
            showError("Energy Baseline (MHP)", `Failed ${method==='put' ? 'editing' : 'adding'} a mhp project`)
         })
      }
   },
};
</script>

<style lang="scss">
.energy-base-line-mhp {
   &.disabled {
      cursor: not-allowed;
      * {
         pointer-events: none;
      }
   }
   font-family: $primary-font;
   color: $font-color;
   > .body {
      padding: 0.6em;
   }
   .step-title {
      span {
         font-family: $secondary-font;
         font-weight: 500;
         font-size: 1.05em;
      }
      font-family: inherit;
      display: flex;
      justify-content: space-between;
   }
   .baseline-radio {
      margin-top: -0.9em;
      padding-left: 1em;
   }
   .note {
      font-size: 0.8em;
   }
   .el-table .selected-row {
      background: tint($primary-color, 85%);
   }
}
</style>
